/**
 * ZEUS v1 — Inline fragrance recommendation concierge
 * Engine preserved; UI layer redesigned for luxury editorial experience.
 */
(function () {
  'use strict';

  if (window.__ZEUS_CONCIERGE_V1__) return;
  window.__ZEUS_CONCIERGE_V1__ = true;

  var TRANSITION_MS = 300;
  var REQUIRED_FIELDS = ['recipient', 'gender', 'occasion', 'season', 'style', 'confidence'];

  var OCCASION_KEYS = {
    daily: 'daily_score',
    office: 'office_score',
    'date-night': 'date_night_score',
    formal: 'formal_score',
    'special-occasion': 'special_occasion_score',
    vacation: 'vacation_score',
    signature: 'signature_score'
  };

  var SEASON_KEYS = {
    spring: 'spring_score',
    summer: 'summer_score',
    autumn: 'autumn_score',
    winter: 'winter_score'
  };

  var STYLE_PATTERNS = {
    fresh: /citrus|aquatic|aromatic|green|fresh spicy|herbal|grapefruit|bergamot|mint|ozonic/,
    clean: /musky|powdery|soapy|aldehydic|white floral|soft spicy|iris|white musk/,
    elegant: /floral|iris|rose|jasmine|aldehydic|violet|chypre|classic/,
    sweet: /sweet|vanilla|gourmand|fruity|caramel|honey|praline|chocolate/,
    woody: /woody|oud|cedar|sandalwood|vetiver|patchouli|guaiac|balsam/,
    amber: /amber|balsamic|warm spicy|resin|labdanum|benzoin|incense/,
    floral: /floral|rose|jasmine|peony|tuberose|ylang|magnolia|lily/,
    powerful: /animalic|leather|smoke|oud|tobacco|rum|dark|intense|beast/,
    'quiet-luxury': /powdery|iris|musk|suede|cashmere|skin|soft|subtle|refined/
  };

  var LABELS = {
    recipient: {
      me: 'for you',
      'someone-else': 'for someone special',
      gift: 'as a gift',
      new: 'for someone new to fragrance'
    },
    occasion: {
      daily: 'daily wear',
      office: 'the office',
      'date-night': 'date night',
      formal: 'formal occasions',
      'special-occasion': 'special occasions',
      vacation: 'vacation',
      signature: 'a signature scent'
    },
    season: {
      spring: 'spring',
      summer: 'summer',
      autumn: 'autumn',
      winter: 'winter',
      'all-year': 'year-round wear'
    },
    style: {
      fresh: 'fresh',
      clean: 'clean',
      elegant: 'elegant',
      sweet: 'sweet',
      woody: 'woody',
      amber: 'amber',
      floral: 'floral',
      powerful: 'powerful',
      'quiet-luxury': 'quiet luxury'
    }
  };

  var OPTION_SELECTOR = '[data-zeus-field][data-value]';

  function optionButtons(root, field) {
    if (field) {
      return root.querySelectorAll('[data-zeus-field="' + field + '"][data-value]');
    }
    return root.querySelectorAll(OPTION_SELECTOR);
  }

  function dedupeProducts(products) {
    var seen = {};
    return (products || []).filter(function (product) {
      if (!product || !product.handle || seen[product.handle]) return false;
      seen[product.handle] = true;
      return true;
    });
  }

  function readData() {
    var el = document.getElementById('zeus-concierge-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function loadCatalog(data) {
    if (!data || !data.catalogUrl) {
      data.products = [];
      return Promise.resolve(data);
    }

    return fetch(data.catalogUrl)
      .then(function (response) {
        if (!response.ok) throw new Error('catalog fetch failed');
        return response.json();
      })
      .then(function (payload) {
        data.products = dedupeProducts(payload.products || payload);
        return data;
      });
  }

  function num(value) {
    var n = Number(value);
    return isNaN(n) ? 0 : n;
  }

  function toArray(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value.map(String);
    if (typeof value === 'string') {
      return value.split(/[,;|/]+/).map(function (s) {
        return s.trim();
      }).filter(Boolean);
    }
    return [String(value)];
  }

  function escapeHtml(str) {
    var d = document.createElement('div');
    d.textContent = String(str || '');
    return d.innerHTML;
  }

  function hasTags(product, tag) {
    return (product.tags || []).some(function (t) {
      return String(t).toLowerCase() === String(tag).toLowerCase();
    });
  }

  function isDiscoveryProduct(product) {
    return product.isDiscoverySet || hasTags(product, 'Discovery Sets') ||
      (product.metafields && product.metafields.zeus_profile_type === 'Discovery Set');
  }

  function hasScoringData(product) {
    var mf = product.metafields || {};
    if (!mf.gender_bias) return false;
    if (!product.image) return false;

    var scoreKeys = [
      'spring_score', 'summer_score', 'autumn_score', 'winter_score',
      'office_score', 'daily_score', 'date_night_score', 'formal_score',
      'special_occasion_score', 'vacation_score', 'signature_score'
    ];

    return scoreKeys.some(function (key) {
      return num(mf[key]) > 0;
    });
  }

  function genderMatch(product, answers) {
    var mf = product.metafields || {};
    var bias = String(mf.gender_bias || '').toLowerCase();
    var selected = answers.gender;

    if (selected === 'none') return 72;

    if (selected === 'men') {
      if (bias === 'men' || hasTags(product, 'Men')) return 100;
      if (bias === 'unisex' || hasTags(product, 'Unisex')) return 78;
      if (bias === 'women' || hasTags(product, 'Women')) return 35;
      return 55;
    }

    if (selected === 'women') {
      if (bias === 'women' || hasTags(product, 'Women')) return 100;
      if (bias === 'unisex' || hasTags(product, 'Unisex')) return 78;
      if (bias === 'men' || hasTags(product, 'Men')) return 35;
      return 55;
    }

    if (selected === 'unisex') {
      if (bias === 'unisex' || hasTags(product, 'Unisex')) return 100;
      return 68;
    }

    return 60;
  }

  function occasionScore(product, answers) {
    var mf = product.metafields || {};
    var key = OCCASION_KEYS[answers.occasion];
    if (!key) return 0;

    var score = num(mf[key]);
    var bonus = 0;

    if (answers.occasion === 'date-night') {
      bonus += num(mf.sillage_score) * 0.12;
      bonus += num(mf.date_night_score) * 0.08;
    }

    if (answers.occasion === 'office') {
      bonus += num(mf.office_score) * 0.08;
      var sillage = num(mf.sillage_score);
      if (sillage > 72) bonus -= (sillage - 72) * 0.35;
    }

    return Math.min(100, score + bonus);
  }

  function seasonScore(product, answers) {
    var mf = product.metafields || {};
    if (answers.season === 'all-year') {
      return (
        num(mf.spring_score) + num(mf.summer_score) +
        num(mf.autumn_score) + num(mf.winter_score)
      ) / 4;
    }

    var key = SEASON_KEYS[answers.season];
    return key ? num(mf[key]) : 0;
  }

  function accordStyleMatch(product, answers) {
    var mf = product.metafields || {};
    var accords = toArray(mf.main_accords).join(' ').toLowerCase();
    if (!accords) return 45;

    var pattern = STYLE_PATTERNS[answers.style];
    if (!pattern) return 50;

    var matches = accords.match(new RegExp(pattern.source, 'gi'));
    var hits = matches ? matches.length : 0;

    if (answers.style === 'quiet-luxury') {
      var sillage = num(mf.sillage_score);
      if (sillage > 0 && sillage < 62) hits += 1;
      if (/powdery|iris|musk|suede/.test(accords)) hits += 2;
    }

    if (answers.style === 'powerful') {
      hits += num(mf.sillage_score) / 35;
      hits += num(mf.longevity_score) / 40;
    }

    if (hits >= 3) return 96;
    if (hits >= 2) return 84;
    if (hits >= 1) return 72;
    return 38;
  }

  function getWeights(answers) {
    var weights = {
      occasion: 0.35,
      season: 0.25,
      gender: 0.15,
      accord: 0.15,
      popularity: 0.10
    };

    if (answers.confidence === 'safe-blind-buy' || answers.confidence === 'popular') {
      weights.popularity = 0.22;
      weights.accord = 0.10;
      weights.gender = 0.13;
    }

    if (answers.confidence === 'unique') {
      weights.popularity = 0.04;
      weights.accord = 0.24;
    }

    return weights;
  }

  function scoreProduct(product, answers) {
    var mf = product.metafields || {};
    var weights = getWeights(answers);

    var occasion = occasionScore(product, answers);
    var season = seasonScore(product, answers);
    var gender = genderMatch(product, answers);
    var accord = accordStyleMatch(product, answers);
    var popularity = num(mf.popularity_score);

    if (answers.confidence === 'popular') {
      popularity = Math.max(popularity, 55);
      if (hasTags(product, 'Popular') || hasTags(product, 'Best Sellers') || hasTags(product, 'Popular This Month')) {
        popularity = Math.min(100, popularity + 18);
      }
    }

    var total =
      occasion * weights.occasion +
      season * weights.season +
      gender * weights.gender +
      accord * weights.accord +
      popularity * weights.popularity;

    return {
      total: total,
      occasion: occasion,
      season: season,
      gender: gender,
      accord: accord,
      popularity: popularity
    };
  }

  function topAccordPhrase(product) {
    var accords = toArray(product.metafields && product.metafields.main_accords);
    if (!accords.length) return 'a balanced profile';
    return accords.slice(0, 2).join(' and ');
  }

  function buildExplanation(product, answers, breakdown) {
    var occasionLabel = LABELS.occasion[answers.occasion] || answers.occasion;
    var seasonLabel = LABELS.season[answers.season] || answers.season;
    var styleLabel = LABELS.style[answers.style] || answers.style;
    var accordPhrase = topAccordPhrase(product);

    if (isDiscoveryProduct(product)) {
      return 'ZEUS suggests beginning with a discovery set — a low-commitment way to explore ' +
        styleLabel + ' profiles ' + (answers.recipient === 'gift' ? 'before gifting' : 'before choosing a full bottle') +
        ', especially when taste is still taking shape.';
    }

    var parts = [
      'ZEUS selected this because it scores highly for ' + seasonLabel + ' and ' + occasionLabel
    ];

    parts.push(', with a ' + styleLabel + ' ' + accordPhrase + ' profile');

    if (answers.occasion === 'office' && breakdown.occasion >= 70) {
      parts.push(' that stays polished without filling the room');
    } else if (answers.occasion === 'date-night' && num(product.metafields.sillage_score) >= 65) {
      parts.push(' with enough presence for evening, without tipping into loud');
    } else if (answers.style === 'quiet-luxury') {
      parts.push(' that feels refined up close rather than performative');
    } else {
      parts.push(' that feels considered and wearable');
    }

    if (answers.confidence === 'safe-blind-buy' || answers.confidence === 'popular') {
      parts.push(', backed by strong popularity signals across the catalogue');
    } else if (answers.confidence === 'unique') {
      parts.push(', with a distinct accord identity rather than a crowd-pleasing formula');
    }

    return parts.join('') + '.';
  }

  function wantsDiscoveryFirst(answers) {
    return answers.confidence === 'discovery-set' ||
      answers.recipient === 'new' ||
      (answers.recipient === 'gift' && answers.confidence !== 'unique');
  }

  function rankProducts(products, answers, config) {
    var fragrancePool = products.filter(function (p) {
      return !isDiscoveryProduct(p) && hasScoringData(p);
    });

    var discoveryPool = products.filter(function (p) {
      return isDiscoveryProduct(p) && p.image;
    });

    if (wantsDiscoveryFirst(answers)) {
      var discoveryRanked = discoveryPool.map(function (product) {
        return {
          product: product,
          score: scoreProduct(product, answers),
          explanation: buildExplanation(product, answers, {})
        };
      }).sort(function (a, b) {
        return b.score.total - a.score.total;
      });

      var discoveryPicks = discoveryRanked.slice(0, Math.min(3, config.maxResults)).map(function (entry) {
        return {
          product: entry.product,
          explanation: entry.explanation,
          score: entry.score.total,
          breakdown: entry.score
        };
      });

      if (discoveryPicks.length >= config.minResults) {
        return discoveryPicks;
      }
    }

    var ranked = fragrancePool.map(function (product) {
      var breakdown = scoreProduct(product, answers);
      return {
        product: product,
        score: breakdown.total,
        breakdown: breakdown,
        explanation: buildExplanation(product, answers, breakdown)
      };
    }).sort(function (a, b) {
      return b.score - a.score;
    });

    var picks = ranked.slice(0, config.maxResults);

    if (picks.length < config.minResults) {
      var fallbackDiscovery = discoveryPool.slice(0, config.maxResults - picks.length).map(function (product) {
        return {
          product: product,
          score: 0,
          breakdown: { total: 0 },
          explanation: 'With no exact match in catalogue, ZEUS recommends exploring through a discovery set first — the gentlest way to find direction without commitment.'
        };
      });
      picks = picks.concat(fallbackDiscovery);
    }

    if (!picks.length && discoveryPool.length) {
      return discoveryPool.slice(0, config.maxResults).map(function (product) {
        var breakdown = scoreProduct(product, answers);
        return {
          product: product,
          score: breakdown.total,
          breakdown: breakdown,
          explanation: buildExplanation(product, answers, breakdown)
        };
      });
    }

    return picks.slice(0, config.maxResults);
  }

  function formatMoney(cents, template) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, template);
    }
    return (cents / 100).toFixed(2);
  }

  function notesLine(value) {
    var items = toArray(value);
    if (!items.length) return '—';
    return items.slice(0, 3).join(', ');
  }

  function confidencePercent(entry) {
    var raw = num(entry.score || (entry.breakdown && entry.breakdown.total));
    if (!raw) return 88;
    return Math.min(99, Math.max(72, Math.round(raw)));
  }

  function buildEditorialCopy(product, answers, entry) {
    var mf = product.metafields || {};
    var accords = toArray(mf.main_accords);
    var accordLead = accords.length ? accords.slice(0, 2).join(' and ') : 'refined accords';
    var styleLabel = LABELS.style[answers.style] || answers.style;
    var occasionLabel = LABELS.occasion[answers.occasion] || answers.occasion;
    var seasonLabel = LABELS.season[answers.season] || answers.season;

    if (isDiscoveryProduct(product)) {
      return accordLead.charAt(0).toUpperCase() + accordLead.slice(1) +
        ' come together in miniature form — an elegant entry point for exploring ' +
        styleLabel + ' character before committing to a full bottle.';
    }

    if (/citrus|aquatic|fresh|aromatic|green/.test(accordLead)) {
      return 'Fresh citrus accords paired with clean musks create a refined scent profile that complements your preference for effortless ' +
        occasionLabel + ' elegance across ' + seasonLabel + ' wear.';
    }

    if (/woody|oud|amber|leather|smoke/.test(accordLead)) {
      return accordLead.charAt(0).toUpperCase() + accordLead.slice(1) +
        ' unfold with quiet depth — a composed signature for ' + occasionLabel +
        ', aligned with your ' + styleLabel + ' direction.';
    }

    if (/floral|rose|iris|jasmine/.test(accordLead)) {
      return 'Floral nuance and soft structure meet in a polished composition suited to ' +
        occasionLabel + ', with a ' + styleLabel + ' sensibility that feels deliberate, never decorative.';
    }

    return accordLead.charAt(0).toUpperCase() + accordLead.slice(1) +
      ' shape a balanced profile for ' + seasonLabel + ' and ' + occasionLabel +
      ', reflecting the ' + styleLabel + ' taste you described.';
  }

  function saveWishlist(handle) {
    try {
      var key = 'zeus_wishlist';
      var list = JSON.parse(localStorage.getItem(key) || '[]');
      if (list.indexOf(handle) === -1) list.unshift(handle);
      localStorage.setItem(key, JSON.stringify(list.slice(0, 24)));
      return true;
    } catch (e) {
      return false;
    }
  }

  function renderCard(entry, template, answers) {
    var fragment = template.content.cloneNode(true);
    var node = fragment.firstElementChild;
    if (!node) return null;

    var product = entry.product;
    var mf = product.metafields || {};

    var link = node.querySelector('[data-zeus-card-link]');
    var image = node.querySelector('[data-zeus-card-image]');
    var vendor = node.querySelector('[data-zeus-card-vendor]');
    var title = node.querySelector('[data-zeus-card-title]');
    var price = node.querySelector('[data-zeus-card-price]');
    var explanation = node.querySelector('[data-zeus-card-explanation]');
    var topNotes = node.querySelector('[data-zeus-top-notes]');
    var heartNotes = node.querySelector('[data-zeus-heart-notes]');
    var baseNotes = node.querySelector('[data-zeus-base-notes]');
    var confidence = node.querySelector('[data-zeus-confidence]');
    var wishlist = node.querySelector('[data-zeus-wishlist]');

    if (link) {
      link.href = product.url;
      link.setAttribute('aria-label', 'View ' + product.title);
    }

    if (image) {
      if (product.image) {
        image.src = product.image;
        image.alt = product.title;
      } else {
        image.remove();
      }
    }

    if (vendor) vendor.textContent = product.vendor || '';
    if (title) title.textContent = product.title;
    if (price) price.textContent = product.priceFormatted || formatMoney(product.price);
    if (explanation) explanation.textContent = buildEditorialCopy(product, answers, entry);
    if (topNotes) topNotes.textContent = notesLine(mf.top_notes);
    if (heartNotes) heartNotes.textContent = notesLine(mf.middle_notes);
    if (baseNotes) baseNotes.textContent = notesLine(mf.base_notes);
    if (confidence) confidence.textContent = confidencePercent(entry) + '%';

    if (wishlist) {
      wishlist.addEventListener('click', function () {
        if (saveWishlist(product.handle)) {
          wishlist.textContent = 'Saved';
          wishlist.disabled = true;
        }
      });
    }

    return node;
  }

  function updateProgress(root, stepIndex, totalSteps) {
    var fill = root.querySelector('[data-zeus-progress]');
    if (!fill) return;
    var pct = Math.round(((stepIndex + 1) / totalSteps) * 100);
    fill.style.width = pct + '%';
  }

  function updateStepLabel(root, stepIndex, totalSteps) {
    var label = root.querySelector('[data-zeus-step-label]');
    if (label) {
      label.textContent = 'Question ' + (stepIndex + 1) + ' of ' + totalSteps;
    }
  }

  function allAnswered(answers) {
    return REQUIRED_FIELDS.every(function (field) {
      return Boolean(answers[field]);
    });
  }

  function buildResultsCopy(answers, count) {
    var lede = 'Curated for ' +
      (LABELS.recipient[answers.recipient] || 'your brief') + ', leaning ' +
      (LABELS.style[answers.style] || answers.style) + ' for ' +
      (LABELS.occasion[answers.occasion] || answers.occasion) + '.';

    if (wantsDiscoveryFirst(answers)) {
      lede = 'Exploration first — a measured way to discover profile before committing to a full bottle.';
    }

    return {
      title: 'Your Recommended Fragrances',
      lede: lede
    };
  }

  function ZeusConcierge(root, data) {
    this.root = root;
    this.data = data;
    this.answers = {};
    this.currentStep = 0;
    this.isTransitioning = false;
    this.template = document.getElementById('zeus-recommendation-card-template');
    this.groups = Array.prototype.slice.call(root.querySelectorAll('[data-zeus-group]'));

    this.intro = root.querySelector('[data-zeus-intro]');
    this.flow = root.querySelector('[data-zeus-flow]');
    this.results = root.querySelector('[data-zeus-results]');
    this.submit = root.querySelector('[data-zeus-submit]');
    this.hint = root.querySelector('[data-zeus-hint]');
    this.cards = root.querySelector('[data-zeus-cards]');

    this.bind();
    this.showStep(0, true);
  }

  ZeusConcierge.prototype.showStep = function (index, immediate) {
    var self = this;
    var total = this.groups.length;

    this.groups.forEach(function (group, i) {
      var isActive = i === index;
      group.hidden = !isActive;
      group.classList.toggle('is-active', isActive);
      group.classList.remove('is-leaving', 'is-entering');
      if (isActive && !immediate) {
        group.classList.add('is-entering');
        window.setTimeout(function () {
          group.classList.remove('is-entering');
        }, TRANSITION_MS);
      }
    });

    this.currentStep = index;
    updateStepLabel(this.root, index, total);
    updateProgress(this.root, index, total);
  };

  ZeusConcierge.prototype.advanceStep = function () {
    var self = this;
    if (this.isTransitioning) return;

    if (this.currentStep >= this.groups.length - 1) {
      if (allAnswered(this.answers)) {
        this.renderResults();
      }
      return;
    }

    this.isTransitioning = true;
    var currentGroup = this.groups[this.currentStep];

    if (currentGroup) {
      currentGroup.classList.add('is-leaving');
    }

    window.setTimeout(function () {
      self.showStep(self.currentStep + 1);
      self.isTransitioning = false;
    }, TRANSITION_MS);
  };

  ZeusConcierge.prototype.startExperience = function () {
    this.intro.hidden = true;
    this.flow.hidden = false;
    this.results.hidden = true;
    this.flow.classList.add('is-visible');
    this.showStep(0, true);
  };

  ZeusConcierge.prototype.bind = function () {
    var self = this;

    this.root.querySelectorAll('[data-zeus-begin]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.startExperience();
      });
    });

    optionButtons(this.root).forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (self.isTransitioning) return;

        var field = btn.getAttribute('data-zeus-field');
        var value = btn.getAttribute('data-value');
        self.answers[field] = value;

        optionButtons(self.root, field).forEach(function (peer) {
          peer.classList.toggle('is-selected', peer === btn);
          peer.setAttribute('aria-pressed', peer === btn ? 'true' : 'false');
        });

        if (self.submit) {
          self.submit.disabled = !allAnswered(self.answers);
        }

        window.setTimeout(function () {
          self.advanceStep();
        }, 180);
      });
    });

    if (this.submit) {
      this.submit.addEventListener('click', function () {
        if (!allAnswered(self.answers)) return;
        self.renderResults();
      });
    }

    this.root.querySelectorAll('[data-zeus-restart]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.reset();
      });
    });

    if (window.location.hash === '#zeus') {
      this.startExperience();
    }
  };

  ZeusConcierge.prototype.renderResults = function () {
    var self = this;
    var config = {
      minResults: num(this.data.minResults) || 3,
      maxResults: num(this.data.maxResults) || 3
    };

    console.log('[ZEUS] 1. answers object', JSON.parse(JSON.stringify(this.answers)));
    console.log('[ZEUS] 2. catalogue size', (this.data.products || []).length);

    var picks = rankProducts(this.data.products || [], this.answers, config);
    console.log('[ZEUS] 3. recommendations returned', picks.length, picks.map(function (p) {
      return p.product && p.product.title;
    }));

    var copy = buildResultsCopy(this.answers, picks.length);

    this.flow.classList.add('is-leaving');
    window.setTimeout(function () {
      self.flow.hidden = true;
      self.flow.classList.remove('is-leaving', 'is-visible');
      self.results.hidden = false;
      self.results.classList.add('is-entering');
      window.setTimeout(function () {
        self.results.classList.remove('is-entering');
      }, TRANSITION_MS);
    }, TRANSITION_MS);

    var titleEl = this.root.querySelector('[data-zeus-results-title]');
    var ledeEl = this.root.querySelector('[data-zeus-results-lede]');

    if (titleEl) titleEl.textContent = copy.title;
    if (ledeEl) ledeEl.textContent = copy.lede;

    console.log('[ZEUS] 4. render targets', {
      template: Boolean(this.template),
      cards: Boolean(this.cards)
    });

    if (this.cards) {
      this.cards.innerHTML = '';
      picks.forEach(function (entry, index) {
        if (!self.template) {
          console.log('[ZEUS] 5. skipped card — template missing');
          return;
        }

        var card = renderCard(entry, self.template, self.answers);
        if (!card) {
          console.log('[ZEUS] 5. skipped card — renderCard returned nothing', index);
          return;
        }

        console.log('[ZEUS] 5. rendering card', index, entry.product && entry.product.title);
        card.style.transitionDelay = (index * 80) + 'ms';
        card.classList.add('is-entering');
        self.cards.appendChild(card);
        window.setTimeout(function () {
          card.classList.remove('is-entering');
        }, TRANSITION_MS + (index * 80));
      });
      console.log('[ZEUS] 6. cards in DOM', self.cards.children.length);
    }

    window.setTimeout(function () {
      self.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, TRANSITION_MS + 60);
  };

  ZeusConcierge.prototype.reset = function () {
    this.answers = {};
    this.currentStep = 0;
    this.isTransitioning = false;

    optionButtons(this.root).forEach(function (btn) {
      btn.classList.remove('is-selected');
      btn.setAttribute('aria-pressed', 'false');
    });

    if (this.submit) this.submit.disabled = true;
    this.results.hidden = true;
    this.results.classList.remove('is-entering');
    this.intro.hidden = false;
    this.flow.hidden = true;
    this.flow.classList.remove('is-visible', 'is-leaving');
    this.showStep(0, true);
  };

  function init() {
    var root = document.querySelector('[data-zeus-concierge]');
    var data = readData();
    if (!root || !data) return;

    var loading = root.querySelector('[data-zeus-loading]');
    var begin = root.querySelector('[data-zeus-begin]');

    if (loading) loading.hidden = false;
    if (begin) begin.disabled = true;

    loadCatalog(data)
      .then(function (loaded) {
        if (loading) loading.hidden = true;
        if (begin) begin.disabled = false;
        new ZeusConcierge(root, loaded);
      })
      .catch(function () {
        if (loading) {
          loading.hidden = false;
          loading.textContent = 'ZEUS could not load the catalogue. Please refresh and try again.';
        }
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
